import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
    Search,
    Plus,
    MoreVertical,
    Eye,
    Filter,
    Boxes,
    TrendingDown,
    PlusCircle,
    RefreshCw,
    Package,
    Archive,
    Trash2
} from "lucide-react";
import axios from "axios";
import { base_api_url } from "../../../environment.jsx";
import AddIngredient from "./AddIngredient.jsx";
import ViewIngredient from "./ViewIngredient.jsx";
import AdjustStockModal from "./AdjustStockModal.jsx";
import ReceptionStockModal from "./ReceptionStockModal.jsx";
import "./InventoryNew.css";

const InventoryNew = () => {
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [ingredients, setIngredients] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [activeTab, setActiveTab] = useState("Tous");
    const [openMenuId, setOpenMenuId] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState("Toutes les catégories");
    const [selectedUnit, setSelectedUnit] = useState("Toutes les unités");
    const [showAddModal, setShowAddModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showAdjustStockModal, setShowAdjustStockModal] = useState(false);
    const [showReceptionModal, setShowReceptionModal] = useState(false);
    const [selectedIngredient, setSelectedIngredient] = useState(null);

    const ideresto = JSON.parse(localStorage.getItem("user"))?.restoId;
    const token = JSON.parse(localStorage.getItem("user"))?.access_token;

    // Listes des catégories et unités
    const categories = [
        "Toutes les catégories",
        "Viandes",
        "Poissons",
        "Légumes",
        "Fruits",
        "Epices",
        "Féculents",
        "Produits laitiers",
        "Boissons",
        "Packaging",
        "Autres"
    ];

    const unites = [
        "Toutes les unités",
        "Grammes (g)",
        "Kilogrammes (kg)",
        "Millilitres (mL)",
        "Litres (L)",
        "Pièce"
    ];

    // Récupérer la liste des ingrédients
    const fetchIngredients = useCallback(async () => {
        if (!ideresto || !token) {
            console.warn("Informations de connexion manquantes");
            return;
        }

        setLoading(true);

        try {
            const response = await axios.get(
                `${base_api_url}api/ingredientRest/${ideresto}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    timeout: 10000,
                }
            );

            if (response.status === 200 || response.status === 201) {
                const data = response.data;
                console.log("Données ingrédients:", data);

                // Extraire le tableau d'ingrédients
                const ingredientsList = data.ingredients || [];
                setIngredients(ingredientsList);
            }
        } catch (error) {
            console.error("Erreur lors du chargement des ingrédients:", error);

            let errorMessage = "Impossible de charger les ingrédients";
            if (error.response) {
                if (error.response.status === 401) {
                    errorMessage = "Session expirée. Veuillez vous reconnecter.";
                } else if (error.response.status === 404) {
                    errorMessage = "Restaurant non trouvé.";
                } else if (error.response.status >= 500) {
                    errorMessage = "Erreur serveur. Veuillez réessayer.";
                }
            } else if (error.code === "ECONNABORTED") {
                errorMessage = "Délai d'attente dépassé.";
            }

            console.error(errorMessage);
            setIngredients([]);
        } finally {
            setLoading(false);
        }
    }, [ideresto, token]);

    useEffect(() => {
        fetchIngredients();
    }, [fetchIngredients, refreshing]);

    // Calculer les jours restants basés sur la consommation des 7 derniers jours
    const calculateRemainingDays = useCallback((item) => {
        if (!item.mouvements || item.mouvements.length === 0) return "---";

        const now = new Date();
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

        // Filtrer les sorties des 7 derniers jours
        const recentExits = item.mouvements.filter((m) => {
            const moveDate = new Date(m.date_mouvement);
            return m.type_mouvement === "SORTIE" && moveDate >= sevenDaysAgo;
        });

        if (recentExits.length === 0) return "∞";

        const totalExits = recentExits.reduce((sum, m) => sum + Math.abs(m.quantite), 0);
        const avgDailyConsumption = totalExits / 7;

        if (avgDailyConsumption === 0) return "∞";

        const currentStock = item.stock?.quantite || 0;
        const daysRemaining = Math.floor(currentStock / avgDailyConsumption);

        return daysRemaining > 999 ? "∞" : daysRemaining.toString();
    }, []);

    // Calculer la consommation des 7 derniers jours
    const calculateConsumption7Days = useCallback((item) => {
        if (!item.mouvements || item.mouvements.length === 0) return "---";

        const now = new Date();
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

        const recentExits = item.mouvements.filter((m) => {
            const moveDate = new Date(m.date_mouvement);
            return m.type_mouvement === "SORTIE" && moveDate >= sevenDaysAgo;
        });

        if (recentExits.length === 0) return "0";

        const total = recentExits.reduce((sum, m) => sum + Math.abs(m.quantite), 0);
        return total.toFixed(2);
    }, []);

    // Calculer les statistiques à partir des données réelles
    const stats = useMemo(() => {
        let totalInStock = 0;

        ingredients.forEach((item) => {
            if (item.stock?.quantite > 0) {
                totalInStock++;
            }
        });

        return [
            {
                label: "Valeur des stocks",
                value: "--- €", // Nécessite prix_unitaire dans l'API
                icon: <Boxes size={20} />,
                colorClass: "icon-orange"
            },
            {
                label: "Articles en stock",
                value: totalInStock.toString(),
                icon: <PlusCircle size={20} />,
                colorClass: "icon-blue"
            },
            {
                label: "Pertes (mois en cours)",
                value: "0 €", // Nécessite données de pertes
                icon: <TrendingDown size={20} />,
                colorClass: "icon-pink"
            }
        ];
    }, [ingredients]);

    // Calculer les compteurs pour les tabs
    const tabs = useMemo(() => {
        const alertThreshold = 10;
        const criticalThreshold = 5;

        const stockOK = ingredients.filter(i => (i.stock?.quantite || 0) > alertThreshold).length;
        const alert = ingredients.filter(i => {
            const qty = i.stock?.quantite || 0;
            return qty <= alertThreshold && qty > criticalThreshold;
        }).length;
        const critical = ingredients.filter(i => (i.stock?.quantite || 0) <= criticalThreshold).length;

        return [
            { label: "Tous", count: ingredients.length, color: "dot-all" },
            { label: "Stock OK", count: stockOK, color: "dot-ok" },
            { label: "Alerte", count: alert, color: "dot-alert" },
            { label: "Critique/Rupture", count: critical, color: "dot-critical" }
        ];
    }, [ingredients]);

    // Filtrer les ingrédients selon la recherche, l'onglet actif, la catégorie et l'unité
    const filteredIngredients = useMemo(() => {
        return ingredients.filter((item) => {
            // Filtre de recherche
            const matchesSearch =
                item.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.reference?.toLowerCase().includes(searchTerm.toLowerCase());

            if (!matchesSearch) return false;

            // Filtre par catégorie
            if (selectedCategory !== "Toutes les catégories") {
                const itemCategory = item.categorie || "Autres";
                if (itemCategory !== selectedCategory) return false;
            }

            // Filtre par unité
            if (selectedUnit !== "Toutes les unités") {
                const unitMap = {
                    "Grammes (g)": "g",
                    "Kilogrammes (kg)": "kg",
                    "Millilitres (mL)": "mL",
                    "Litres (L)": "L",
                    "Pièce": "pièce"
                };
                const selectedUnitShort = unitMap[selectedUnit];
                if (item.unite !== selectedUnitShort) return false;
            }

            // Filtre par statut de stock
            const stockQuantity = item.stock?.quantite || 0;

            // Seuils d'alerte
            const alertThreshold = 10;
            const criticalThreshold = 5;

            if (activeTab === "Tous") return true;
            if (activeTab === "Stock OK") return stockQuantity > alertThreshold;
            if (activeTab === "Alerte") return stockQuantity <= alertThreshold && stockQuantity > criticalThreshold;
            if (activeTab === "Critique/Rupture") return stockQuantity <= criticalThreshold;

            return true;
        });
    }, [ingredients, searchTerm, activeTab, selectedCategory, selectedUnit]);

    // Gérer le rafraîchissement
    const handleRefresh = () => {
        setRefreshing((prev) => !prev);
    };

    // Handlers pour les actions
    const handleViewIngredient = (ingredient) => {
        console.log("Voir ingrédient:", ingredient);
        setSelectedIngredient(ingredient);
        setShowViewModal(true);
        setOpenMenuId(null);
    };

    const handleAdjustStock = (ingredient) => {
        console.log("Ajuster stock:", ingredient);
        setSelectedIngredient(ingredient);
        setShowAdjustStockModal(true);
        setOpenMenuId(null);
    };

    const handleReception = (ingredient) => {
        console.log("Réception stock:", ingredient);
        if (ingredient) {
            // Ouvrir le modal avec l'ingrédient pré-sélectionné
            setSelectedIngredient(ingredient);
        }
        setShowReceptionModal(true);
        setOpenMenuId(null);
    };

    const handleArchive = (ingredient) => {
        console.log("Archiver ingrédient:", ingredient);
        setOpenMenuId(null);
        // TODO: Archiver
    };

    const handleDelete = (ingredient) => {
        console.log("Supprimer ingrédient:", ingredient);
        setOpenMenuId(null);
        // TODO: Confirmer et supprimer
    };

    const toggleMenu = (ingredientId) => {
        setOpenMenuId(openMenuId === ingredientId ? null : ingredientId);
    };

    // Fermer le menu si on clique ailleurs
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openMenuId && !event.target.closest('.action-menu-container')) {
                setOpenMenuId(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openMenuId]);

    return (
        <div className="page-wrapper">
            <div className="content">
                <div className="inventory-container">
                    {/* Header Section */}
                    <div className="inventory-header">
                        <div className="header-left">
                            <h1>Inventaire & Stock</h1>
                            <p>Suivez vos ingrédients, consommations et alertes</p>
                        </div>
                        <div className="header-right">
                            <button
                                className="btn-icon-only"
                                title="Rafraîchir"
                                onClick={handleRefresh}
                                disabled={loading}
                            >
                                <RefreshCw size={18} className={refreshing ? 'spinning' : ''} />
                            </button>
                            {/* Boutons cachés temporairement */}
                            {/* <button
                                className="btn-secondary-outline"
                                onClick={() => handleReception(null)}
                            >
                                <Boxes size={18} />
                                Réception stock
                            </button>
                            <button
                                className="btn-primary-orange"
                                onClick={() => setShowAddModal(true)}
                            >
                                <Plus size={16} className="d-md-none" />
                                <span className="d-none d-md-inline">Ajouter un ingrédient</span>
                            </button> */}
                        </div>
                    </div>

                    {/* Stats Cards Row */}
                    <div className="inventory-stats">
                        {stats.map((stat, index) => (
                            <div className="stat-card" key={index}>
                                <div className="stat-content">
                                    <span className="stat-label">{stat.label}</span>
                                    <span className="stat-value">{stat.value}</span>
                                </div>
                                <div className={`stat-icon ${stat.colorClass}`}>
                                    {stat.icon}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Filters & Search Section */}
                    <div className="inventory-filters">
                        <div className="filter-row">
                            <div className="search-wrapper">
                                <Search className="search-icon" size={18} />
                                <input
                                    type="text"
                                    className="search-input"
                                    placeholder="Rechercher un ingrédient, SKU, fournisseur..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <div style={{ display: 'flex', gap: '12px' }}>
                                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                    <Filter size={16} style={{ position: 'absolute', left: '12px', color: '#6b7280', pointerEvents: 'none' }} />
                                    <select
                                        className="filter-select"
                                        style={{ paddingLeft: '36px' }}
                                        value={selectedCategory}
                                        onChange={(e) => setSelectedCategory(e.target.value)}
                                    >
                                        {categories.map((cat, idx) => (
                                            <option key={idx} value={cat}>{cat}</option>
                                        ))}
                                    </select>
                                </div>
                                <select
                                    className="filter-select"
                                    value={selectedUnit}
                                    onChange={(e) => setSelectedUnit(e.target.value)}
                                >
                                    {unites.map((unit, idx) => (
                                        <option key={idx} value={unit}>{unit}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="status-tabs">
                            {tabs.map((tab, index) => (
                                <div
                                    key={index}
                                    className={`status-tab ${activeTab === tab.label ? 'active' : ''}`}
                                    onClick={() => setActiveTab(tab.label)}
                                >
                                    <span className={`dot ${tab.color}`}></span>
                                    {tab.label}
                                    <span className="count">{tab.count}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Inventory Table Card */}
                    <div className="inventory-table-card">
                        <div className="table-responsive">
                            {loading ? (
                                <div style={{ padding: '40px', textAlign: 'center' }}>
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Chargement...</span>
                                    </div>
                                </div>
                            ) : (
                                <table className="custom-table">
                                    <thead>
                                        <tr>
                                            <th>Ingrédient</th>
                                            <th>Code / Réf</th>
                                            <th>Stock dispo</th>
                                            <th>Unité</th>
                                            <th>Seuil</th>
                                            <th>Statut</th>
                                            <th>Conso 7j</th>
                                            <th>Jours restants</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredIngredients.length === 0 ? (
                                            <tr>
                                                <td colSpan="9" style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                                                    Aucun ingrédient trouvé
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredIngredients.map((item) => {
                                                const stockQuantity = item.stock?.quantite || 0;
                                                const consumption7d = calculateConsumption7Days(item);
                                                const daysRemaining = calculateRemainingDays(item);

                                                // Déterminer le statut et le badge en fonction du stock
                                                const alertThreshold = 10;
                                                const criticalThreshold = 5;
                                                let statusBadge = {
                                                    label: "OK",
                                                    className: "status-badge-ok"
                                                };

                                                if (stockQuantity === 0) {
                                                    statusBadge = { label: "Rupture", className: "status-badge-rupture" };
                                                } else if (stockQuantity <= criticalThreshold) {
                                                    statusBadge = { label: "Critique", className: "status-badge-critique" };
                                                } else if (stockQuantity <= alertThreshold) {
                                                    statusBadge = { label: "Alerte", className: "status-badge-alerte" };
                                                }

                                                return (
                                                    <tr key={item.id_ingredient}>
                                                        <td className="ingredient-cell">
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                {item.image ? (
                                                                    <img
                                                                        src={`${base_api_url}api/recuperer/${item.image}`}
                                                                        alt={item.nom}
                                                                        style={{
                                                                            width: '32px',
                                                                            height: '32px',
                                                                            borderRadius: '8px',
                                                                            objectFit: 'cover',
                                                                            backgroundColor: '#f3f4f6'
                                                                        }}
                                                                        onError={(e) => {
                                                                            e.target.style.display = 'none';
                                                                            e.target.nextSibling.style.display = 'flex';
                                                                        }}
                                                                    />
                                                                ) : null}
                                                                <div style={{
                                                                    width: '32px',
                                                                    height: '32px',
                                                                    borderRadius: '8px',
                                                                    backgroundColor: '#f3f4f6',
                                                                    display: item.image ? 'none' : 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    fontSize: '14px',
                                                                    fontWeight: '700',
                                                                    color: '#ff6a3d'
                                                                }}>
                                                                    {item.nom.charAt(0).toUpperCase()}
                                                                </div>
                                                                {item.nom}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                                <span style={{ fontSize: '13px', fontWeight: '600' }}>{item.code || "---"}</span>
                                                                <span style={{ fontSize: '11px', color: '#9ca3af' }}>{item.reference || "---"}</span>
                                                            </div>
                                                        </td>
                                                        <td style={{ fontWeight: '700', color: stockQuantity === 0 ? '#ef4444' : '#1f2937' }}>
                                                            {stockQuantity}
                                                        </td>
                                                        <td>
                                                            <span className="category-badge">{item.unite || "unité"}</span>
                                                        </td>
                                                        <td>{alertThreshold}</td>
                                                        <td>
                                                            <span className={statusBadge.className}>{statusBadge.label}</span>
                                                        </td>
                                                        <td>{consumption7d}</td>
                                                        <td style={{ fontWeight: '600', color: daysRemaining === "∞" ? '#10b981' : (parseInt(daysRemaining) < 7 ? '#ef4444' : '#1f2937') }}>
                                                            {daysRemaining}
                                                        </td>
                                                        <td>
                                                            <div className="action-buttons">
                                                                <button
                                                                    className="action-btn"
                                                                    title="Voir"
                                                                    onClick={() => handleViewIngredient(item)}
                                                                >
                                                                    <Eye size={16} />
                                                                </button>
                                                                <button
                                                                    className="action-btn"
                                                                    title="Ajuster stock"
                                                                    onClick={() => handleAdjustStock(item)}
                                                                >
                                                                    <Plus size={16} />
                                                                </button>
                                                                <div className="action-menu-container">
                                                                    <button
                                                                        className="action-btn"
                                                                        title="Plus d'actions"
                                                                        onClick={() => toggleMenu(item.id_ingredient)}
                                                                    >
                                                                        <MoreVertical size={16} />
                                                                    </button>
                                                                    {openMenuId === item.id_ingredient && (
                                                                        <div className="context-menu">
                                                                            <button
                                                                                className="context-menu-item"
                                                                                onClick={() => handleReception(item)}
                                                                            >
                                                                                <Package size={16} />
                                                                                <span>Réception</span>
                                                                            </button>
                                                                            <button
                                                                                className="context-menu-item"
                                                                                onClick={() => handleArchive(item)}
                                                                            >
                                                                                <Archive size={16} />
                                                                                <span>Archiver</span>
                                                                            </button>
                                                                            <button
                                                                                className="context-menu-item danger"
                                                                                onClick={() => handleDelete(item)}
                                                                            >
                                                                                <Trash2 size={16} />
                                                                                <span>Supprimer</span>
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Add Ingredient Modal */}
            <AddIngredient
                show={showAddModal}
                onClose={() => setShowAddModal(false)}
                onSuccess={() => {
                    setRefreshing((prev) => !prev); // Trigger refresh
                }}
            />

            {/* View Ingredient Modal */}
            <ViewIngredient
                show={showViewModal}
                onClose={() => {
                    setShowViewModal(false);
                    setSelectedIngredient(null);
                }}
                ingredient={selectedIngredient}
                onUpdate={() => {
                    setRefreshing((prev) => !prev); // Trigger refresh
                }}
            />

            {/* Adjust Stock Modal */}
            <AdjustStockModal
                show={showAdjustStockModal}
                onClose={() => {
                    setShowAdjustStockModal(false);
                    setSelectedIngredient(null);
                }}
                ingredient={selectedIngredient}
                onSuccess={() => {
                    setShowAdjustStockModal(false);
                    setSelectedIngredient(null);
                    setRefreshing((prev) => !prev); // Trigger refresh
                }}
            />

            {/* Reception Stock Modal */}
            <ReceptionStockModal
                show={showReceptionModal}
                onClose={() => {
                    setShowReceptionModal(false);
                    setSelectedIngredient(null);
                }}
                ingredients={ingredients}
                onSuccess={() => {
                    setShowReceptionModal(false);
                    setSelectedIngredient(null);
                    setRefreshing((prev) => !prev); // Trigger refresh
                }}
            />
        </div>
    );
};

export default InventoryNew;
